import {DiplomatObject} from 'types/DiplomatObject';
import * as yup from 'common/yup';
import i18n from 'i18n';
import {SftpPermissions} from './SftpPermissions';
import {SftpAuthRules} from './SftpAuthRules';
import {SftpPasswordPolicies} from './SftpPasswordPolicies';
import {defaultPortSchema} from 'common/yup/customs/portSchema';
import {portSchema} from 'types/transport/Abstract/AbstractServer';
import {ipOrHosString, ipString} from 'common/yup/customs/ip';
import {uniq} from 'lodash';
import {IPAccessRulesSettings} from './IPAccessRulesSettings';
import {WebServerSettings} from './WebServerSettings';

export const SftpServerSettingsNames = {
   enabled: 'enabled',
   address: 'address',
   port: 'port',
   sshKeyId: 'sshKeyId',
   sshKeyIds: 'sshKeyIds',
   rootDirectory: 'rootDirectory',

   defaultPermissions: 'defaultPermissions',
   authRules: 'authRules',
   passwordPolicies: 'passwordPolicies',

   deleteOldFiles: 'deleteOldFiles',
   deleteAgeInDays: 'deleteAgeInDays',

   keyExchanges: 'keyExchanges',
   ciphers: 'ciphers',
   hmacs: 'hmacs',
   compressions: 'compressions',
   signatures: 'signatures',

   useGateway: 'useGateway',
   edgeIP: 'edgeIP',
   pncPort: 'pncPort',
   gconnID: 'gconnID',

   accessRule: 'accessRule',

   // allowSftpThroughEG: 'allowSftpThroughEG',
   // allowWebTransferThroughEG: 'allowWebTransferThroughEG',
} as const;

export const SftpServerSettings = WebServerSettings.shape({
   folderId: yup.string(),
   [SftpServerSettingsNames.enabled]: yup.boolean(),

   [SftpServerSettingsNames.deleteOldFiles]: yup.boolean(),
   [SftpServerSettingsNames.deleteAgeInDays]: yup.number(),

   [SftpServerSettingsNames.address]: ipString(),
   [SftpServerSettingsNames.port]: defaultPortSchema(),
   [SftpServerSettingsNames.rootDirectory]: yup.string(),
   [SftpServerSettingsNames.sshKeyId]: yup.string(),
   [SftpServerSettingsNames.sshKeyIds]: yup.array().of(yup.string()),
   [SftpServerSettingsNames.defaultPermissions]: SftpPermissions,
   [SftpServerSettingsNames.authRules]: SftpAuthRules,
   [SftpServerSettingsNames.passwordPolicies]: SftpPasswordPolicies,
   [SftpServerSettingsNames.keyExchanges]: yup.array().of(yup.string()),
   [SftpServerSettingsNames.ciphers]: yup.array().of(yup.string()),
   [SftpServerSettingsNames.hmacs]: yup.array().of(yup.string()),
   [SftpServerSettingsNames.compressions]: yup.array().of(yup.string()),

   [SftpServerSettingsNames.useGateway]: yup.bool().default(false),
   [SftpServerSettingsNames.edgeIP]: ipOrHosString(),
   [SftpServerSettingsNames.pncPort]: portSchema(),
   [SftpServerSettingsNames.gconnID]: yup.string(),

   // [SftpServerSettingsNames.allowSftpThroughEG]: yup.bool(),
   // [SftpServerSettingsNames.allowWebTransferThroughEG]: yup.bool(),

   [SftpServerSettingsNames.accessRule]: IPAccessRulesSettings,
}).transform(function (val, orig) {
   // Support legacy sshKeyId
   if (val.sshKeyId) {
      val.sshKeyIds = uniq([val.sshKeyId, ...(val.sshKeyIds || [])]);
      val.sshKeyId = '';
   }

   return val;
});
export type ISftpServerSettings = yup.InferType<typeof SftpServerSettings>;

export function validateSftpServerSettings(values: ISftpServerSettings) {
   const errors = {} as ISftpServerSettings;
   if (values[SftpServerSettingsNames.enabled]) {
      if (!values[SftpServerSettingsNames.sshKeyIds]?.length) {
         errors[SftpServerSettingsNames.sshKeyIds] = i18n.t('SSH Host Key must be specified');
      }
   }

   return errors;
}
